/**
 * DiscoverProspectsPanel — Section A of the Prospects tab.
 * May 19, 2026. Additive only — sits ABOVE the existing manual list.
 *
 * Calls backend:
 *   POST /api/prospects/search    (Apollo mixed_people/api_search)
 *   POST /api/prospects/enrich    (Apollo people/match)
 *
 * NO Apollo branding in this UI. Use "Reveal Email", "Intelligence Credit",
 * "Email not found", etc.
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import {
  Search, Sparkles, Mail, Phone, ChevronDown, ChevronUp, Save,
  Plus, Linkedin, Loader2, Users, Send, BadgeCheck, Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const CITY_OPTIONS = [
  'All Canada', 'Toronto', 'Vancouver', 'Calgary',
  'Ottawa', 'Montreal',
];

// Map UI city labels to Apollo `person_locations` query strings. Apollo
// filters geography most precisely when the value is the full
// "City, Province, Country" string. "All Canada" widens to the country.
const CITY_TO_APOLLO_LOCATION = {
  'All Canada': 'Canada',
  'Toronto':    'Toronto, Ontario, Canada',
  'Vancouver':  'Vancouver, British Columbia, Canada',
  'Calgary':    'Calgary, Alberta, Canada',
  'Ottawa':     'Ottawa, Ontario, Canada',
  'Montreal':   'Montreal, Quebec, Canada',
};

const SIZE_OPTIONS = [
  { label: '1-10',    value: '1,10' },
  { label: '11-50',   value: '11,50' },
  { label: '51-200',  value: '51,200' },
  { label: '201-500', value: '201,500' },
  { label: '500+',    value: '501,10000' },
];

// Apollo returns full Canadian province NAMES ("Ontario", "British
// Columbia") — not codes. Map to standard 2-letter codes for the card
// location line so we render "Vancouver, BC" not "Vancouver, British
// Columbia". Unknown values pass through unchanged (so US states like
// "California" still render as-is rather than blank).
const PROVINCE_TO_CODE = {
  'Alberta':                   'AB',
  'British Columbia':          'BC',
  'Manitoba':                  'MB',
  'New Brunswick':             'NB',
  'Newfoundland and Labrador': 'NL',
  'Nova Scotia':               'NS',
  'Ontario':                   'ON',
  'Prince Edward Island':      'PE',
  'Quebec':                    'QC',
  'Saskatchewan':              'SK',
  'Northwest Territories':     'NT',
  'Nunavut':                   'NU',
  'Yukon':                     'YT',
};

function formatProvince(stateValue) {
  if (!stateValue) return '';
  return PROVINCE_TO_CODE[stateValue] || stateValue;
}

export function DiscoverProspectsPanel({
  api,
  initialCompany = '',
  initialTitle = '',
  autoRun = false,
  onAddToManualProspects,
  onAddToContacts,
  onGenerateOutreach,
}) {
  const [expanded, setExpanded] = useState(true);
  const [jobTitle, setJobTitle] = useState(initialTitle);
  const [company, setCompany] = useState(initialCompany);
  const [city, setCity] = useState('All Canada');
  const [companySize, setCompanySize] = useState('');
  const [industry, setIndustry] = useState('');

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  // Per-result reveal state: { [idx]: {email, phone, emailRevealed, phoneRevealed, revealing} }
  const [reveals, setReveals] = useState({});

  // Selection state for bulk actions: Set<idx>
  const [selected, setSelected] = useState(new Set());

  // Save-as-list inputs
  const [newListName, setNewListName] = useState('');
  const [savingList, setSavingList] = useState(false);
  const [myLists, setMyLists] = useState([]);
  const [creditTally, setCreditTally] = useState(0);

  const loadMyLists = useCallback(async () => {
    try {
      const data = await api.get('/prospect-lists');
      setMyLists(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadMyLists(); }, [loadMyLists]);

  // Sync initialCompany updates (e.g. when user clicks "Find contacts" on a signal card)
  useEffect(() => {
    if (initialCompany) setCompany(initialCompany);
  }, [initialCompany]);

  // Feb 2026 — sync initialTitle updates (e.g. when user clicks "Find
  // Decision Maker" on a hiring signal). Reuses the same `useEffect`
  // pattern as initialCompany above.
  useEffect(() => {
    if (initialTitle) setJobTitle(initialTitle);
  }, [initialTitle]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReveals({});
    setSelected(new Set());
    try {
      const body = { per_page: 25 };
      if (jobTitle.trim()) body.job_titles = [jobTitle.trim()];
      if (company.trim()) body.organization_names = [company.trim()];
      // Always send a Canada-scoped person_locations value so Apollo can't
      // leak US/UK/etc. results. Map UI labels → "City, Province, Canada".
      const apolloLocation = CITY_TO_APOLLO_LOCATION[city] || 'Canada';
      body.person_locations = [apolloLocation];
      if (companySize) body.organization_num_employees_ranges = [companySize];
      if (industry.trim()) body.organization_industries = [industry.trim()];

      const res = await api.post('/prospects/search', body);
      setResults(res?.contacts || []);
      if (!res?.contacts?.length) {
        setError('No contacts found. Try widening your filters.');
      }
    } catch (e) {
      setError(e?.message || 'Search failed. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [api, jobTitle, company, city, companySize, industry]);

  // Auto-run search when called from a signal card click.
  // Feb 2026 — also trigger when initialTitle alone is supplied (e.g. the
  // "Find Decision Maker" flow may sometimes have a role but no company).
  useEffect(() => {
    if (autoRun && (initialCompany || initialTitle)) {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, initialCompany, initialTitle]);

  async function revealEmail(idx) {
    const c = results[idx];
    const url = c?.linkedin_url;
    if (!url) {
      // Defensive — the button is also disabled in this case so this branch
      // should normally not fire, but keep it for safety.
      setReveals((r) => ({
        ...r,
        [idx]: { ...(r[idx] || {}), emailRevealed: true, email: null,
                 emailError: null, emailNoLinkedin: true, revealing: null },
      }));
      return;
    }
    // Mark in-flight (this drives the spinner state on the button)
    setReveals((r) => ({
      ...r,
      [idx]: { ...(r[idx] || {}), revealing: 'email', emailError: null },
    }));
    // Client-side 25s safety net so the button can never spin forever, even
    // if upstream Apollo or the backend hangs.
    const timeoutId = setTimeout(() => {
      setReveals((r) => {
        const cur = r[idx] || {};
        if (cur.revealing !== 'email') return r; // already resolved
        return {
          ...r,
          [idx]: { ...cur, revealing: null,
                   emailError: 'Unable to retrieve — try again' },
        };
      });
    }, 25000);
    try {
      const res = await api.post('/prospects/enrich', { linkedin_url: url });
      clearTimeout(timeoutId);
      const email = res?.person?.email || null;
      // Feb 2026 — enrich also returns city/state; merge them back into the
      // displayed contact so the location line populates after Reveal.
      // Apollo's search endpoint paywalls city/state behind enrichment.
      const enrichedCity = res?.person?.city || null;
      const enrichedState = res?.person?.state || null;
      if (enrichedCity || enrichedState) {
        setResults((rs) => rs.map((row, i) => (
          i === idx
            ? { ...row, city: row.city || enrichedCity, state: row.state || enrichedState }
            : row
        )));
      }
      setReveals((r) => ({
        ...r,
        [idx]: { ...(r[idx] || {}), email, emailRevealed: true,
                 emailError: null, revealing: null },
      }));
      if (email) {
        setCreditTally((n) => n + 1);
        toast.success('1 intelligence credit used');
      }
      // Silent on "not found" — the inline label "Email not found" is enough.
    } catch (e) {
      clearTimeout(timeoutId);
      setReveals((r) => ({
        ...r,
        [idx]: { ...(r[idx] || {}), revealing: null,
                 emailError: 'Unable to retrieve — try again' },
      }));
    }
  }

  async function copyEmail(email) {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      toast.success('Email copied');
    } catch {
      toast.error('Could not copy');
    }
  }

  async function revealPhone(idx) {
    const c = results[idx];
    const url = c?.linkedin_url;
    if (!url) {
      setReveals((r) => ({
        ...r,
        [idx]: { ...(r[idx] || {}), phoneRevealed: true, phone: null,
                 phoneError: null, phoneNoData: true, revealing: null },
      }));
      return;
    }
    setReveals((r) => ({
      ...r,
      [idx]: { ...(r[idx] || {}), revealing: 'phone', phoneError: null },
    }));
    const timeoutId = setTimeout(() => {
      setReveals((r) => {
        const cur = r[idx] || {};
        if (cur.revealing !== 'phone') return r;
        return {
          ...r,
          [idx]: { ...cur, revealing: null,
                   phoneError: 'Unable to retrieve — try again' },
        };
      });
    }, 25000);
    try {
      const res = await api.post('/prospects/enrich', { linkedin_url: url });
      clearTimeout(timeoutId);
      const phone = res?.person?.phone_number || null;
      // Feb 2026 — also pull city/state from enrich (same as revealEmail).
      const enrichedCity = res?.person?.city || null;
      const enrichedState = res?.person?.state || null;
      if (enrichedCity || enrichedState) {
        setResults((rs) => rs.map((row, i) => (
          i === idx
            ? { ...row, city: row.city || enrichedCity, state: row.state || enrichedState }
            : row
        )));
      }
      setReveals((r) => ({
        ...r,
        [idx]: { ...(r[idx] || {}), phone, phoneRevealed: true,
                 phoneError: null, revealing: null },
      }));
      if (phone) {
        setCreditTally((n) => n + 8);
        toast.success('8 intelligence credits used');
      }
    } catch (e) {
      clearTimeout(timeoutId);
      setReveals((r) => ({
        ...r,
        [idx]: { ...(r[idx] || {}), revealing: null,
                 phoneError: 'Unable to retrieve — try again' },
      }));
    }
  }

  async function copyPhone(phone) {
    if (!phone) return;
    try {
      await navigator.clipboard.writeText(phone);
      toast.success('Phone copied');
    } catch {
      toast.error('Could not copy');
    }
  }

  function toggleSelect(idx) {
    setSelected((s) => {
      const out = new Set(s);
      if (out.has(idx)) out.delete(idx); else out.add(idx);
      return out;
    });
  }

  function buildContactPayload(idx) {
    const c = results[idx] || {};
    const r = reveals[idx] || {};
    return {
      first_name: c.first_name,
      last_name: c.last_name,
      title: c.title,
      organization_name: c.organization_name,
      city: c.city,
      linkedin_url: c.linkedin_url,
      email: r.email || c.email || null,
      phone: r.phone || c.phone_number || null,
      source: 'search',
    };
  }

  async function saveCurrentResultsAsList() {
    const name = newListName.trim();
    if (!name) {
      toast.error('Give your list a name first');
      return;
    }
    if (!results.length) {
      toast.error('Run a search first');
      return;
    }
    setSavingList(true);
    try {
      const list = await api.post('/prospect-lists', { name });
      const payload = results.map((_, idx) => buildContactPayload(idx));
      await api.post(`/prospect-lists/${list.id}/contacts`, payload);
      toast.success(`Saved ${payload.length} contacts to "${name}"`);
      setNewListName('');
      loadMyLists();
    } catch {
      toast.error('Could not save list');
    } finally {
      setSavingList(false);
    }
  }

  async function saveSelectedToList(listId) {
    if (!selected.size) {
      toast.error('Select contacts first');
      return;
    }
    try {
      const payload = Array.from(selected).map((idx) => buildContactPayload(idx));
      const res = await api.post(`/prospect-lists/${listId}/contacts`, payload);
      toast.success(
        `Added ${res?.inserted ?? payload.length} to list` +
        (res?.skipped_duplicates ? ` (${res.skipped_duplicates} duplicate${res.skipped_duplicates === 1 ? '' : 's'} skipped)` : '')
      );
      loadMyLists();
    } catch {
      toast.error('Could not save to list');
    }
  }

  return (
    <Card
      className="border-[#B89B5E]/30 bg-gradient-to-br from-[#FAF4E6]/30 to-white"
      data-testid="discover-prospects-panel"
    >
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <div className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-[#FAF4E6]/30">
            <div className="flex items-center gap-3">
              <BadgeCheck className="w-5 h-5 text-[#B89B5E]" />
              <div>
                <div className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
                  Intelligence
                </div>
                <h2 className="font-serif text-xl text-[#1A1A1A]">Discover Prospects</h2>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs px-2 py-1 bg-[#B89B5E]/15 text-[#8A6F2E] uppercase tracking-wider rounded-sm">
                FREE search
              </span>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-5 pt-2">
            {/* Filters row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Job Title</Label>
                <Input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="Sales Manager"
                  data-testid="discover-job-title"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company Name</Label>
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Optional"
                  data-testid="discover-company"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">City</Label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="discover-city"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CITY_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Company Size</Label>
                <Select value={companySize} onValueChange={setCompanySize}>
                  <SelectTrigger data-testid="discover-size"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    {SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Industry</Label>
                <Input
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="Optional"
                  data-testid="discover-industry"
                />
              </div>
              <div className="flex flex-col justify-end">
                <Button
                  onClick={runSearch}
                  disabled={loading}
                  className="bg-[#B89B5E] hover:bg-[#A88B4E] text-white rounded-sm"
                  data-testid="discover-search-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching...</>
                  ) : (
                    <><Search className="w-4 h-4 mr-2" />Search Prospects</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Search free · Reveal email <span className="font-medium">1cr</span> · Phone <span className="font-medium">8cr</span>
                </p>
                {creditTally > 0 && (
                  <p className="text-xs text-[#8A6F2E] mt-1" data-testid="credit-tally">
                    {creditTally} intelligence credits used this session
                  </p>
                )}
              </div>
            </div>

            {error && (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2"
                   data-testid="discover-error">{error}</div>
            )}

            {/* Save current results as a named list */}
            {results.length > 0 && (
              <div className="flex items-center gap-2 p-3 bg-[#FAF4E6]/30 border border-[#B89B5E]/20 rounded-sm">
                <Save className="w-4 h-4 text-[#B89B5E]" />
                <Input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Save these results as a named list..."
                  className="flex-1"
                  data-testid="save-as-list-name"
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={savingList}
                  onClick={saveCurrentResultsAsList}
                  data-testid="save-as-list-btn"
                >
                  {savingList ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save list'}
                </Button>
              </div>
            )}

            {/* Bulk action bar */}
            {selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 p-3 bg-[#1A1A1A] text-white rounded-sm" data-testid="bulk-action-bar">
                <span className="text-sm font-medium">{selected.size} of {results.length} selected</span>
                <div className="flex gap-2 ml-auto">
                  <Select onValueChange={saveSelectedToList}>
                    <SelectTrigger className="w-44 bg-white text-[#1A1A1A]" data-testid="bulk-save-to-list">
                      <SelectValue placeholder={`Save ${selected.size} to list`} />
                    </SelectTrigger>
                    <SelectContent>
                      {myLists.length === 0 && (
                        <SelectItem value="__none__" disabled>No lists yet — create one above</SelectItem>
                      )}
                      {myLists.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name} ({l.contact_count || 0})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm" variant="secondary"
                    onClick={() => {
                      Array.from(selected).forEach((idx) => onAddToContacts?.(buildContactPayload(idx)));
                      toast.success(`Adding ${selected.size} to contacts`);
                    }}
                    data-testid="bulk-add-contacts"
                  >
                    <Users className="w-3.5 h-3.5 mr-1" />Add to Contacts
                  </Button>
                </div>
              </div>
            )}

            {/* Results grid */}
            {results.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="discover-results">
                {results.map((c, idx) => {
                  const r = reveals[idx] || {};
                  return (
                    <Card
                      key={idx}
                      className="border-[#E8E6E1] hover:border-[#B89B5E]/40 transition-colors"
                      data-testid={`discover-result-${idx}`}
                    >
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selected.has(idx)}
                            onCheckedChange={() => toggleSelect(idx)}
                            className="mt-1"
                            data-testid={`discover-result-${idx}-checkbox`}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-[#1A1A1A]">
                              {[c.first_name, c.last_name].filter(Boolean).join(' ') || '(name hidden)'}
                            </div>
                            <div className="text-sm text-muted-foreground">{c.title || ''}</div>
                            <div className="text-sm font-medium">{c.organization_name || ''}</div>
                            {(c.city || c.state) && (
                              <div
                                className="text-xs text-muted-foreground"
                                data-testid={`discover-result-${idx}-location`}
                              >
                                {[c.city, formatProvince(c.state)].filter(Boolean).join(', ')}
                              </div>
                            )}
                          </div>
                          {c.linkedin_url && (
                            <a
                              href={c.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#0A66C2] hover:text-[#084e95]"
                              title="View on LinkedIn"
                              data-testid={`discover-result-${idx}-linkedin`}
                            >
                              <Linkedin className="w-4 h-4" />
                            </a>
                          )}
                        </div>

                        {/* Reveal email — states:
                            • loading → spinner button (disabled)
                            • success + email → email shown inline + copy icon (no button)
                            • success + no email → "Email not found" inline (no button)
                            • error → inline error + Try again button
                            • default → "Reveal Email (1 credit)" active button (always
                              clickable regardless of linkedin_url — backend handles)
                        */}
                        <div className="flex items-center gap-2">
                          {r.revealing === 'email' ? (
                            <Button
                              size="sm" variant="outline"
                              className="border-[#B89B5E] text-[#8A6F2E]"
                              disabled
                              data-testid={`discover-result-${idx}-reveal-email`}
                            >
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              Revealing...
                            </Button>
                          ) : r.emailRevealed && r.email ? (
                            <span
                              className="text-sm font-mono text-[#1A1A1A] flex items-center gap-2 flex-1"
                              data-testid={`discover-result-${idx}-email`}
                            >
                              {r.email}
                              <button
                                type="button"
                                onClick={() => copyEmail(r.email)}
                                className="text-[#B89B5E] hover:text-[#8A6F2E] p-1 rounded-sm hover:bg-[#FAF4E6]"
                                title="Copy email"
                                data-testid={`discover-result-${idx}-copy-email`}
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ) : r.emailRevealed && !r.email ? (
                            <span
                              className="text-sm text-muted-foreground italic flex-1"
                              data-testid={`discover-result-${idx}-email-not-found`}
                            >
                              Email not found
                            </span>
                          ) : r.emailError ? (
                            <div className="flex items-center gap-2 flex-1">
                              <span
                                className="text-sm text-amber-700 italic"
                                data-testid={`discover-result-${idx}-email-error`}
                              >
                                {r.emailError}
                              </span>
                              <Button
                                size="sm" variant="outline"
                                className="border-[#B89B5E] text-[#8A6F2E] hover:bg-[#FAF4E6]"
                                onClick={() => revealEmail(idx)}
                                data-testid={`discover-result-${idx}-reveal-email`}
                              >
                                <Mail className="w-3.5 h-3.5 mr-1" />
                                Try again
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm" variant="outline"
                              className="border-[#B89B5E] text-[#8A6F2E] hover:bg-[#FAF4E6]"
                              onClick={() => revealEmail(idx)}
                              data-testid={`discover-result-${idx}-reveal-email`}
                            >
                              <Mail className="w-3.5 h-3.5 mr-1" />
                              Reveal Email <span className="ml-1 text-xs">(1 credit)</span>
                            </Button>
                          )}
                        </div>

                        {/* Reveal phone — same states as email */}
                        <div className="flex items-center gap-2">
                          {r.revealing === 'phone' ? (
                            <Button
                              size="sm" variant="outline"
                              className="border-[#B89B5E] text-[#8A6F2E]"
                              disabled
                              data-testid={`discover-result-${idx}-reveal-phone`}
                            >
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              Revealing...
                            </Button>
                          ) : r.phoneRevealed && r.phone ? (
                            <span
                              className="text-sm font-mono text-[#1A1A1A] flex items-center gap-2 flex-1"
                              data-testid={`discover-result-${idx}-phone`}
                            >
                              {r.phone}
                              <button
                                type="button"
                                onClick={() => copyPhone(r.phone)}
                                className="text-[#B89B5E] hover:text-[#8A6F2E] p-1 rounded-sm hover:bg-[#FAF4E6]"
                                title="Copy phone"
                                data-testid={`discover-result-${idx}-copy-phone`}
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ) : r.phoneRevealed && !r.phone ? (
                            <span
                              className="text-sm text-muted-foreground italic flex-1"
                              data-testid={`discover-result-${idx}-phone-not-found`}
                            >
                              Phone not found
                            </span>
                          ) : r.phoneError ? (
                            <div className="flex items-center gap-2 flex-1">
                              <span
                                className="text-sm text-amber-700 italic"
                                data-testid={`discover-result-${idx}-phone-error`}
                              >
                                {r.phoneError}
                              </span>
                              <Button
                                size="sm" variant="outline"
                                className="border-[#B89B5E] text-[#8A6F2E] hover:bg-[#FAF4E6]"
                                onClick={() => revealPhone(idx)}
                                data-testid={`discover-result-${idx}-reveal-phone`}
                              >
                                <Phone className="w-3.5 h-3.5 mr-1" />
                                Try again
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm" variant="outline"
                              className="border-[#B89B5E] text-[#8A6F2E] hover:bg-[#FAF4E6]"
                              onClick={() => revealPhone(idx)}
                              data-testid={`discover-result-${idx}-reveal-phone`}
                            >
                              <Phone className="w-3.5 h-3.5 mr-1" />
                              Reveal Phone <span className="ml-1 text-xs">(8 credits)</span>
                            </Button>
                          )}
                        </div>

                        {/* Card actions */}
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E8E6E1]">
                          <Button
                            size="sm" variant="ghost" className="text-xs h-7"
                            onClick={() => onAddToManualProspects?.(c)}
                            data-testid={`discover-result-${idx}-add-prospect`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add to My Prospects
                          </Button>
                          <Button
                            size="sm" variant="ghost" className="text-xs h-7"
                            onClick={() => onAddToContacts?.(buildContactPayload(idx))}
                            data-testid={`discover-result-${idx}-add-contact`}
                          >
                            <Users className="w-3 h-3 mr-1" /> Add to Contacts
                          </Button>
                          <Button
                            size="sm" variant="ghost" className="text-xs h-7"
                            onClick={() => onGenerateOutreach?.(buildContactPayload(idx))}
                            disabled={!r.emailRevealed || !r.email}
                            title={r.emailRevealed && r.email ? 'Compose outreach' : 'Reveal email first'}
                            data-testid={`discover-result-${idx}-outreach`}
                          >
                            <Send className="w-3 h-3 mr-1" /> Generate Outreach
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {!loading && !results.length && !error && (
              <p className="text-sm text-muted-foreground italic" data-testid="discover-empty">
                Enter filters above and click <span className="font-medium">Search Prospects</span> to discover decision-makers.
              </p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
